'use client';

import { useEffect, useRef, useState, useMemo } from 'react';
import { Chart, registerables } from 'chart.js';
import usersIcon from '../../assets/icons/usersIcon.svg';
import documentIcon from '../../assets/icons/documentIcon.svg';
import StatCard from '../../components/shared/StatCard';
import AccessManagementTable from '../../components/shared/AccessManagementTable';
import AuditLogsTable from '../../components/shared/AuditLogsTable';
import SubAdminModal from '../../components/shared/subAdminModal';
import DeleteIcon from '../../assets/icons/delete.svg';
import ConfirmationModal from '../../components/modals/confirmationModal';
import { AdminService } from '../../services';
import { useAuthContext } from '../../hooks/useAuth';
import Modal from '../../components/modals/modal';
import SuccessIcon from '../../assets/icons/profile-updated-modal-icon.svg'; // New import for the success icon
import Pagination from '../../components/shared/Pagination'; // Import Pagination component
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { Toast } from '../../components/shared/Toast';
import 'react-loading-skeleton/dist/skeleton.css';
import AccessManagementTableSkeleton from '../../components/shared/AccessManagementTableSkeleton';

Chart.register(...registerables);

export default function AdminDashboard() {
	const chartRef = useRef(null);
	const chartInstance = useRef(null);
	const [subAdminModalOpen, setSubAdminModalOpen] = useState(false);
	const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
	const [isModalEditMode, setIsModalEditMode] = useState(false);
	const [currentSubAdminData, setCurrentSubAdminData] = useState(null);
	const [subAdminToDelete, setSubAdminToDelete] = useState(null);
	const [subAdmins, setSubAdmins] = useState([]);
	const { getUserDisplayName } = useAuthContext();
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [modalMessage, setModalMessage] = useState('');
	const [modalHeading, setModalHeading] = useState(''); // New state for dynamic modal heading
	const [isDeleting, setIsDeleting] = useState(false); // New state for delete loading
	const navigate = useNavigate(); // Initialize useNavigate
	const [isLoadingData, setIsLoadingData] = useState(false); // New state for data loading
	const [documentCount, setDocumentCount] = useState(0);
	const [isDocsLoading, setIsDocsLoading] = useState(false);
	const [activeUsersCount, setActiveUsersCount] = useState(0);
	const [totalUsersCount, setTotalUsersCount] = useState(0);

	// Pagination states for Access Management Table
	const [currentPageAccess, setCurrentPageAccess] = useState(1);
	const docsPerPageAccess = 5;

	// Pagination states for Audit Logs Table
	const [currentPageAudit, setCurrentPageAudit] = useState(1);
	const docsPerPageAudit = 5;

	// Example: raw monthly data for users and projects
	const rawData = useMemo(
		() => ({
			users: [120, 450, 680, 320, 180, 420, 580, 220, 750, 520, 380, 650],
			projects: [80, 280, 420, 580, 720, 380, 480, 520, 180, 450, 320, 280]
		}),
		[]
	);

	// Convert raw numbers → percentages (relative per-month)
	const userPercentages = rawData.users.map((val) => Math.round((val / 900) * 100)); // 900 = max scale
	const projectPercentages = rawData.projects.map((val) => Math.round((val / 900) * 100));

	const chartData = useMemo(
		() => ({
			labels: [
				'Jan 2019',
				'Feb 2019',
				'Mar 2019',
				'Apr 2019',
				'May 2019',
				'Jun 2019',
				'Jul 2019',
				'Aug 2019',
				'Sep 2019',
				'Oct 2019',
				'Nov 2019',
				'Dec 2019'
			],
			datasets: [
				{
					label: 'Users',
					data: userPercentages,
					rawData: rawData.users, // Add raw data for the tooltip
					backgroundColor: '#F36E49',
					borderWidth: 0
				},
				{
					label: 'Projects',
					data: projectPercentages,
					rawData: rawData.projects, // Add raw data for the tooltip
					backgroundColor: '#8B5CF6',
					borderWidth: 0
				}
			]
		}),
		[userPercentages, projectPercentages, rawData]
	);

	useEffect(() => {
		if (chartRef.current) {
			if (chartInstance.current) chartInstance.current.destroy();

			const ctx = chartRef.current.getContext('2d');

			// Plugin for chart background
			const backgroundPlugin = {
				id: 'custom_canvas_background_color',
				beforeDraw: (chart) => {
					const { ctx, chartArea } = chart;
					ctx.save();
					ctx.fillStyle = '#F8F2FC80'; // semi-transparent background
					ctx.fillRect(
						chartArea.left,
						chartArea.top,
						chartArea.right - chartArea.left,
						chartArea.bottom - chartArea.top
					);
					ctx.restore();
				}
			};

			chartInstance.current = new Chart(ctx, {
				type: 'bar',
				data: chartData,
				plugins: [backgroundPlugin],
				options: {
					responsive: true,
					maintainAspectRatio: false,
					scales: {
						x: {
							stacked: true,
							grid: {
								display: false // hide vertical grid lines
							},
							ticks: { color: '#6B7280', font: { size: 12 } }
						},
						y: {
							stacked: true,
							beginAtZero: true,
							max: 100,
							ticks: {
								color: '#6B7280',
								font: { size: 12 },
								stepSize: 25,
								callback: (value) => `${value}%`
							},
							grid: {
								color: '#E8E8EA',
								borderDash: [4, 4] // dashed horizontal lines
							}
						}
					},
					plugins: {
						legend: {
							display: false
						},
						tooltip: {
							mode: 'index',
							intersect: false,
							backgroundColor: '#FFFFFF',
							titleColor: '#180A63',
							bodyColor: '#180A63',
							borderColor: '#E8E8EA',
							borderWidth: 1,
							cornerRadius: 8,
							displayColors: true,
							position: 'nearest',
							callbacks: {
								title: (context) => context[0].label,
								label: (context) => {
									const datasetIndex = context.datasetIndex;
									const dataIndex = context.dataIndex;
									const rawValue = chartData.datasets[datasetIndex].rawData[dataIndex];
									return `${context.dataset.label}: ${rawValue}`;
								}
							}
						}
					},
					interaction: {
						mode: 'index',
						intersect: false
					}
				}
			});
		}

		return () => {
			if (chartInstance.current) chartInstance.current.destroy();
		};
	}, [chartData]);

	useEffect(() => {
		getSubAdmins();
		getDocumentCount(true);
		getUsersCount(true);
		getUsersCount(false);
	}, []);

	const getSubAdmins = async () => {
		setIsLoadingData(true); // Set loading to true
		try {
			const res = await AdminService.getSubAdmins();
			setSubAdmins(res.data.users);
		} catch (error) {
			console.error('Error fetching sub-admins:', error);
		} finally {
			setIsLoadingData(false); // Set loading to false
		}
	};

	const getUsersCount = async (isActive) => {
		setIsLoadingData(true); // Set loading to true
		try {
			const res = await AdminService.getUsersCount(isActive);
			if (isActive) {
				setActiveUsersCount(res.data.customersCount);
			} else {
				setTotalUsersCount(res.data.customersCount);
			}
		} finally {
			setIsLoadingData(false); // Set loading to false
		}
	};

	const getDocumentCount = async (count) => {
		setIsDocsLoading(true); // Set loading to true
		try {
			const res = await AdminService.getDocumentsCount(count);
			setDocumentCount(res.data.documentsCount);
		} finally {
			setIsDocsLoading(false); // Set loading to false
		}
	};

	const accessUsers = subAdmins.map((subAdmin) => ({
		name: subAdmin.firstName + ' ' + subAdmin.lastName,
		email: subAdmin.email,
		role: subAdmin.roleType || '',
		status: subAdmin.status,
		_id: subAdmin._id, // Include the user ID for edit/delete operations
		firstName: subAdmin.firstName, // Add firstName directly
		lastName: subAdmin.lastName // Add lastName directly
	}));

	// Apply pagination to accessUsers
	const indexOfLastAccessUser = currentPageAccess * docsPerPageAccess;
	const indexOfFirstAccessUser = indexOfLastAccessUser - docsPerPageAccess;
	const currentAccessUsers = accessUsers.slice(indexOfFirstAccessUser, indexOfLastAccessUser);

	const auditLogs = [
		{
			activity: 'Logged in',
			comment: 'Logged into the sub-admin',
			email: 'majidali@nesl-it.com',
			datetime: '12/10/2025, 2:30 PM'
		},
		{
			activity: 'Resolve query',
			comment: 'Resolved query #0981',
			email: 'muhammadhaseeb@nesl-it.com',
			datetime: '12/12/2025, 9:23 AM'
		},
		{
			activity: 'User Registration',
			comment: 'New user registered',
			email: 'alex.brown@example.com',
			datetime: '11/28/2025, 11:00 AM'
		},
		{
			activity: 'Document Deleted',
			comment: 'Document removed from system',
			email: 'lisa.davis@example.com',
			datetime: '11/22/2025, 3:30 PM'
		},
		{
			activity: 'Settings Updated',
			comment: 'System settings modified',
			email: 'admin@example.com',
			datetime: '10/25/2025, 1:15 PM'
		}
	];

	// Apply pagination to auditLogs
	const indexOfLastAuditLog = currentPageAudit * docsPerPageAudit;
	const indexOfFirstAuditLog = indexOfLastAuditLog - docsPerPageAudit;
	const currentAuditLogs = auditLogs.slice(indexOfFirstAuditLog, indexOfLastAuditLog);

	const handleAddSubAdmin = async (newSubAdminData) => {
		try {
			const res = await AdminService.addSubAdmin(newSubAdminData);
			if (res.status === 200) {
				const { firstName, lastName } = newSubAdminData;
				setModalMessage(
					<>
						User "<span className="text-dark-blue font-medium">{`${firstName} ${lastName}`}</span>"
						added successfully
					</>
				);
				setModalHeading('User added successfully'); // Set heading for successful add
				setShowSuccessModal(true);
				getSubAdmins(); // Refresh the list of sub-admins
				return true; // Indicate success
			} else {
				setModalMessage(res.message || 'Failed to add user.');
				setShowErrorModal(true);
				return false; // Indicate failure
			}
		} catch (error) {
			console.error('Error adding user:', error);
			const errorMessage = error.response?.data?.message || 'Network error. Please try again.';
			setModalMessage(errorMessage);
			setShowErrorModal(true);
			return false; // Indicate failure
		}
	};

	const handleEditSubAdmin = (user) => {
		setSubAdminModalOpen(true);
		setIsModalEditMode(true);
		setCurrentSubAdminData(user);
	};

	const handleDeleteSubAdmin = (user) => {
		setDeleteConfirmationModalOpen(true);
		setSubAdminToDelete(user);
	};

	const handleUpdateSubAdmin = async (subAdminId, dataToUpdate) => {
		try {
			// Ensure we have a valid userId before proceeding
			if (!subAdminId) {
				console.error('User ID is missing for update.');
				Toast.error('Error: Sub-admin ID is missing for update.');
				return false;
			}

			// Validate first name and last name length
			if (dataToUpdate.firstName && dataToUpdate.firstName.length < 3) {
				Toast.error('Minimum length for first name is 3 characters');
				return false;
			}
			if (dataToUpdate.lastName && dataToUpdate.lastName.length < 3) {
				Toast.error('Minimum length for last name is 3 characters');
				return false;
			}

			const res = await AdminService.updateSubAdmin(subAdminId, dataToUpdate);
			if (res.status === 200) {
				// Determine if it was an update or deactivation
				if (dataToUpdate.status === 'inactive') {
					const subAdminName = currentSubAdminData?.name || 'Sub-admin';
					setModalMessage(
						<>
							Sub-admin "<span className="text-dark-blue font-medium">{subAdminName}</span>"
							deactivated successfully
						</>
					);
					setModalHeading('Sub-admin deactivated successfully'); // Set heading for deactivation
				} else {
					// For updates, we use the firstName and lastName from dataToUpdate
					setModalMessage(
						<>
							Sub-admin "
							<span className="text-dark-blue font-medium">{`${
								dataToUpdate.firstName || currentSubAdminData?.firstName
							} ${dataToUpdate.lastName || currentSubAdminData?.lastName}`}</span>
							" updated successfully
						</>
					);
					setModalHeading('Sub-admin updated successfully'); // Set heading for update
				}
				setShowSuccessModal(true);
				getSubAdmins(); // Refresh the list of sub-admins
				return true; // Indicate success
			} else {
				Toast.error(res.message || 'Failed to update.');
				return false; // Indicate failure
			}
		} catch (error) {
			console.error('Error updating sub-admin:', error);
			const errorMessage = error.response?.data?.message || 'Network error. Please try again.';
			Toast.error(errorMessage);
			return false; // Indicate failure
		}
	};

	const handleDeleteSubAdminConfirm = async () => {
		if (!subAdminToDelete?._id) {
			console.error('Sub-admin ID is missing for deletion.');
			setModalMessage('Error: Sub-admin ID is missing for deletion.');
			setModalHeading('Deletion Failed');
			setShowErrorModal(true);
			setDeleteConfirmationModalOpen(false);
			return;
		}

		setIsDeleting(true);
		try {
			const res = await AdminService.deleteSubAdmin(subAdminToDelete._id);

			if (res.status === 200) {
				setModalMessage(
					<>
						Sub-admin "
						<span className="text-dark-blue font-medium">
							{subAdminToDelete.firstName} {subAdminToDelete.lastName}
						</span>
						" deleted successfully
					</>
				);
				setModalHeading('Sub-admin deleted successfully');
				setShowSuccessModal(true);
				getSubAdmins(); // Refresh the list
			} else {
				Toast.error(res.message || 'Failed to delete.');
			}
		} catch (error) {
			console.error('Error deleting sub-admin:', error);
			const errorMessage = error.response?.data?.message || 'Network error. Please try again.';
			Toast.error(errorMessage);
		} finally {
			setIsDeleting(false);
			setDeleteConfirmationModalOpen(false);
		}
	};

	return (
		<div className="">
			{/* Header Section */}
			<div className="flex flex-col items-center md:flex-row md:justify-between md:items-center gap-4 mb-10 md:mb-[50px]">
				<h1 className="text-2xl sm:text-3xl font-semibold text-dark-blue text-center md:text-left">
					Welcome, {getUserDisplayName()}
				</h1>

				<button
					className="inline-flex items-center space-x-2 bg-dark-blue max-w-[210px] h-[52px] py-5 px-4 text-base font-semibold leading-100 text-white rounded-md cursor-pointer"
					onClick={() => {
						setSubAdminModalOpen(true);
						setIsModalEditMode(false);
						setCurrentSubAdminData(null);
					}}
				>
					<svg
						width="12"
						height="12"
						viewBox="0 0 12 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M6.5625 0.875C6.5625 0.391016 6.17148 0 5.6875 0C5.20352 0 4.8125 0.391016 4.8125 0.875V4.8125H0.875C0.391016 4.8125 0 5.20352 0 5.6875C0 6.17148 0.391016 6.5625 0.875 6.5625H4.8125V10.5C4.8125 10.984 5.20352 11.375 5.6875 11.375C6.17148 11.375 6.5625 10.984 6.5625 10.5V6.5625H10.5C10.984 6.5625 11.375 6.17148 11.375 5.6875C11.375 5.20352 10.984 4.8125 10.5 4.8125H6.5625V0.875Z"
							fill="white"
						/>
					</svg>
					<span>Add User</span>
				</button>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10 md:mb-[50px]">
				<StatCard
					icon={usersIcon}
					bgColor="bg-lightGrey"
					textColor="text-dark-blue"
					label="Total register users"
					value={totalUsersCount}
					newValue="5,541"
					onRefresh={() => getUsersCount(false)}
					isRefreshing={isLoadingData}
					isLoading={isLoadingData}
				/>
				<StatCard
					icon={usersIcon}
					bgColor="bg-lightGrey"
					textColor="text-green-600"
					label="Active paid users"
					value={activeUsersCount}
					newValue="281"
					onRefresh={() => getUsersCount(true)}
					isRefreshing={isLoadingData}
					isLoading={isLoadingData}
				/>
				<StatCard
					icon={documentIcon}
					bgColor="bg-lightGrey"
					textColor="text-purple-600"
					label="Total projects"
					value={documentCount}
					newValue="14,543"
					onRefresh={() => getDocumentCount(true)}
					isRefreshing={isDocsLoading}
					isLoading={isDocsLoading}
				/>
			</div>

			{/* Chart */}
			<div className="bg-white rounded-xl p-6 shadow-sm border-2 border-lightGrey mb-10 md:mb-[50px]">
				<div className="flex justify-between items-center mb-6">
					<h2 className="text-xl font-semibold text-dark-blue">Usage chart</h2>
					<select className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm text-dark-blue focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent">
						<option value="monthly">Monthly</option>
						<option value="weekly">Weekly</option>
						<option value="yearly">Yearly</option>
					</select>
				</div>
				<div className="h-80">
					<canvas ref={chartRef}></canvas>
				</div>
			</div>
			<div className="flex justify-between flex-col md:flex-row items-center mb-12">
				<div>
					<h1 className="text-[22px] font-medium text-dark-blue">Access Management</h1>
				</div>

				<div>
					<a
						href="/admin/access-management"
						className="inline-flex items-center gap-2 text-dark-blue text-20 underline leading-24"
					>
						View all
					</a>
				</div>
			</div>

			{isLoadingData ? (
				<AccessManagementTableSkeleton docsPerPage={docsPerPageAccess} />
			) : currentAccessUsers.length > 0 ? (
				<AccessManagementTable
					accessUsers={currentAccessUsers}
					onEditSubAdmin={handleEditSubAdmin}
					onDeleteSubAdmin={handleDeleteSubAdmin}
				/>
			) : (
				<div className="flex justify-center items-center h-40 text-lg text-dark-blue">
					No Users found.
				</div>
			)}

			{currentAccessUsers.length > 0 && (
				<Pagination
					totalDocs={accessUsers.length}
					docsPerPage={docsPerPageAccess}
					onPageChange={setCurrentPageAccess}
				/>
			)}

			<AuditLogsTable heading={'Audit Logs'} auditLogs={currentAuditLogs} />
			<Pagination
				totalDocs={auditLogs.length}
				docsPerPage={docsPerPageAudit}
				onPageChange={setCurrentPageAudit}
			/>
			<SubAdminModal
				isOpen={subAdminModalOpen}
				onClose={() => setSubAdminModalOpen(false)}
				isEdit={isModalEditMode}
				initialFirstName={currentSubAdminData?.firstName} // Use direct firstName
				initialLastName={currentSubAdminData?.lastName} // Use direct lastName
				initialEmail={currentSubAdminData?.email}
				userId={currentSubAdminData?._id} // Pass the userId to the modal
				currentStatus={currentSubAdminData?.status} // Pass current status to the modal
				initialRole={currentSubAdminData?.role} // Pass the user's current role
				onAddSubAdmin={handleAddSubAdmin} // Pass the handler here
				onUpdateSubAdmin={handleUpdateSubAdmin} // New prop for updating sub-admin (now handles deactivation)
			/>

			<ConfirmationModal
				isOpen={deleteConfirmationModalOpen}
				onClose={() => setDeleteConfirmationModalOpen(false)}
				icon={<img src={DeleteIcon} alt="Delete" className="mx-auto" />}
				heading="Deleting User"
				description={
					<>
						Are you sure you want to delete{' '}
						<span className="text-dark-blue font-medium">{subAdminToDelete?.name}</span> from users?
					</>
				}
				primaryButtonText="Cancel"
				secondaryButtonText="Delete"
				onPrimaryClick={() => setDeleteConfirmationModalOpen(false)}
				onSecondaryClick={handleDeleteSubAdminConfirm} // Call the new handler
				isLoading={isDeleting} // Pass loading state
			/>

			<Modal
				isOpen={showSuccessModal}
				icon={<img src={SuccessIcon} alt="Success" className="mx-auto" />}
				heading={modalHeading} // Use the dynamic heading state
				description={modalMessage}
				buttonText="View Users"
				onButtonClick={() => {
					setShowSuccessModal(false);
					navigate('/admin/access-management'); // Navigate to access management page
				}}
			/>

			<Modal
				isOpen={showErrorModal}
				heading="Error"
				description={modalMessage}
				buttonText="Got it"
				onButtonClick={() => setShowErrorModal(false)}
			/>
		</div>
	);
}
